/**
 * An image.
 *
 * Most images are FluidCloud links — absolute URLs the browser can just load,
 * with no token involved — so those go straight into <img src> and get the
 * browser's own cache and lazy loading. A relative `/api/media/…` src is the
 * fallback route, which needs a bearer token: that one is fetched through the
 * API client (cached per path) and swapped in as an object URL.
 */
import { useEffect, useState } from 'react';
import { mediaUrl } from '../lib/api';

export interface ImgProps {
  src: string | null | undefined;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
}

export default function Img({ src, alt, className, style }: ImgProps) {
  const direct = src?.startsWith('http') ? src : null;
  const [resolved, setResolved] = useState<string | null>(direct);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) { setResolved(null); return; }
    setFailed(false);
    if (src.startsWith('http')) { setResolved(src); return; }
    let active = true;
    setResolved(null);
    void mediaUrl(src)
      .then((url) => { if (active) setResolved(url); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [src]);

  return (
    <span className={`frame ${className ?? ''}`} style={style}>
      {resolved && (
        <img src={resolved} alt={alt} loading="lazy" onError={() => setFailed(true)} />
      )}
      {failed && (
        <span
          className="faint mono"
          style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontSize: 10 }}
        >
          missing
        </span>
      )}
    </span>
  );
}
